import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './index.css';

const ProtectedRoute = ({ 
  children, 
  requireAuth = false, 
  requireAdmin = false, 
  allowedRoles = [] 
}) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      try {
        console.log('🔍 ProtectedRoute: 开始检查权限');
        
        const currentUserStr = localStorage.getItem('currentUser');
        const token = localStorage.getItem('token');
        
        console.log('🔍 ProtectedRoute: localStorage 数据', {
          hasUser: !!currentUserStr,
          hasToken: !!token
        });

        if (!currentUserStr || !token) {
          console.log('❌ ProtectedRoute: 未找到用户信息或 token');
          setIsAuthenticated(false);
          setUserRole('');
          
          if (requireAuth) {
            console.log('⚠️ ProtectedRoute: 需要认证，重定向到登录页');
            setShouldRedirect(`/auth?redirect=${encodeURIComponent(location.pathname)}`);
          }
        } else {
          try {
            const userData = JSON.parse(currentUserStr);
            console.log('✅ ProtectedRoute: 用户数据', {
              username: userData.username,
              isLoggedIn: userData.isLoggedIn,
              role: userData.role
            });

            if (!userData.isLoggedIn) {
              console.log('❌ ProtectedRoute: 用户未登录状态');
              setIsAuthenticated(false);
              setUserRole('');
              if (requireAuth) {
                setShouldRedirect(`/auth?redirect=${encodeURIComponent(location.pathname)}`);
              }
            } else {
              console.log('✅ ProtectedRoute: 用户已登录');
              setIsAuthenticated(true);
              setUserRole(userData.role || 'user');
            }
          } catch (error) {
            console.error('❌ ProtectedRoute: 解析用户数据失败', error);
            setIsAuthenticated(false);
            setUserRole('');
            if (requireAuth) {
              setShouldRedirect(`/auth?redirect=${encodeURIComponent(location.pathname)}`);
            }
          }
        }
      } catch (error) {
        console.error('❌ ProtectedRoute: 检查权限出错', error);
        setIsAuthenticated(false);
        setUserRole('');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // 监听 localStorage 变化
    const handleStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [location.pathname, requireAuth]);

  // 添加 CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .protected-route-loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
        background: linear-gradient(135deg, #f9f7fe 0%, #f0f9ff 100%);
        flex-direction: column;
        gap: 20px;
      }
      .loading-icon {
        color: #ff85c0;
        animation: spin 1s linear infinite;
      }
      .loading-text {
        color: #ff85c0;
        font-size: 16px;
        font-weight: 500;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  if (loading) {
    return (
      <div className="protected-route-loading">
        <Spin 
          indicator={
            <LoadingOutlined className="loading-icon" style={{ fontSize: 24 }} spin />
          } 
        />
        <span className="loading-text">验证中...</span>
      </div>
    );
  }

  if (shouldRedirect) {
    console.log('🔀 ProtectedRoute: 重定向到', shouldRedirect);
    return <Navigate to={shouldRedirect} replace />;
  }

  // 检查管理员权限
  if (requireAuth && requireAdmin && userRole !== 'admin') {
    console.log('❌ ProtectedRoute: 需要管理员权限但用户不是管理员', { 
      userRole, 
      requireAdmin,
      isAuthenticated 
    });
    message.error('需要管理员权限才能访问！');
    return <Navigate to="/" replace state={{ error: '需要管理员权限' }} />;
  }

  // 检查特定角色权限
  if (requireAuth && allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    console.log('❌ ProtectedRoute: 用户角色不符合要求', { 
      userRole, 
      allowedRoles 
    });
    message.error('权限不足！');
    return <Navigate to="/" replace state={{ error: '权限不足' }} />;
  }

  console.log('✅ ProtectedRoute: 权限验证通过', { 
    isAuthenticated, 
    userRole,
    requireAuth,
    requireAdmin 
  });
  return children;
};

export default ProtectedRoute;